/**
 * IT Ticket View Page
 *
 * Full-page ticket detail view for IT staff.
 * Features:
 * - Two-column layout (details + activities)
 * - Public comments and internal work notes
 * - Status changes with waiting reasons
 * - Escalation and assignment management
 * - Microsoft Fluent-inspired design
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useTicketing } from '../../contexts/TicketingContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketRepository, useActivityRepository, useTechRepository } from '../../data/hooks';
import { TicketDetailsPanel } from './components/TicketDetailsPanel';
import { ActivityPanel } from './components/ActivityPanel';
import type { Ticket, Tech } from '../../types/ticket';
import type { TicketActivity } from '../../types/ticketActivity';
import {
  TicketStatus,
  WaitingReason,
  AssignmentGroup,
} from '../../types/ticket';
import { getSLAStatus } from '../../types/ticketActivity';
import './ITTicketViewPage.css';

export function ITTicketViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { getTechId } = useAuth();
  const ticketRepo = useTicketRepository();
  const activityRepo = useActivityRepository();
  const techRepo = useTechRepository();
  const {
    currentUserId,
    currentUser,
    snackbar,
    reassignDrawerOpen,
    handleAddComment,
    handleAddWorkNote,
    handleAskQuestion,
    handleStatusChange,
    handleEscalateTicket,
    handleReassignTicket,
    handleAssignTicket,
  } = useTicketing();

  // Get the current tech's ID for assignment comparison
  const currentTechId = getTechId();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [activities, setActivities] = useState<TicketActivity[]>([]);
  const [techs, setTechs] = useState<Tech[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  // Load ticket, activities, and techs
  useEffect(() => {
    async function loadData() {
      if (!id) {
        setError('Ticket not found');
        setIsLoading(false);
        return;
      }

      try {
        const [loadedTicket, loadedActivities, loadedTechs] = await Promise.all([
          ticketRepo.getById(id),
          activityRepo.getByTicketId(id),
          techRepo.getAll(),
        ]);

        if (!loadedTicket) {
          setError('Ticket not found');
          setIsLoading(false);
          return;
        }

        setTicket(loadedTicket);
        setActivities(loadedActivities);
        setTechs(loadedTechs);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load ticket:', err);
        setError('Failed to load ticket details');
        setIsLoading(false);
      }
    }

    loadData();
  }, [id, ticketRepo, activityRepo, techRepo]);

  // Track previous drawer state for detecting close
  const prevDrawerOpenRef = useRef(reassignDrawerOpen);

  // Refresh ticket data when reassign drawer closes (indicating action completed or cancelled)
  useEffect(() => {
    const wasOpen = prevDrawerOpenRef.current;
    const isClosed = !reassignDrawerOpen;

    // If drawer was open and is now closed, refresh the data
    if (wasOpen && isClosed && id) {
      (async () => {
        const [updatedTicket, updatedActivities] = await Promise.all([
          ticketRepo.getById(id),
          activityRepo.getByTicketId(id),
        ]);
        if (updatedTicket) setTicket(updatedTicket);
        setActivities(updatedActivities);
      })();
    }

    prevDrawerOpenRef.current = reassignDrawerOpen;
  }, [reassignDrawerOpen, id, ticketRepo, activityRepo]);

  // Refresh activities
  const refreshActivities = useCallback(async () => {
    if (!id) return;
    const updated = await activityRepo.getByTicketId(id);
    setActivities(updated);
  }, [id, activityRepo]);

  // Calculate SLA status
  const slaStatus = useMemo(() => {
    if (!ticket) return null;
    return getSLAStatus(ticket.slaDeadline, ticket.status);
  }, [ticket]);

  // Resolve assigned tech from techId
  const assignedTech = useMemo(() => {
    if (!ticket?.assignedTechId) return null;
    return techs.find((t) => t.id === ticket.assignedTechId) ?? null;
  }, [ticket?.assignedTechId, techs]);

  // Determine assignment state for dynamic action button
  const isUnassigned = !ticket?.assignedTechId;
  const isAssignedToMe = ticket?.assignedTechId === currentTechId;
  const isAssignedToOther = !isUnassigned && !isAssignedToMe;

  // Full refresh of ticket and activities
  const refreshTicketData = useCallback(async () => {
    if (!id) return;
    const [updatedTicket, updatedActivities] = await Promise.all([
      ticketRepo.getById(id),
      activityRepo.getByTicketId(id),
    ]);
    if (updatedTicket) setTicket(updatedTicket);
    setActivities(updatedActivities);
  }, [id, ticketRepo, activityRepo]);

  // Handle "Assign to me" action
  const onAssignToMe = useCallback(async () => {
    if (!ticket) {
      console.error('Cannot assign: No ticket loaded');
      return;
    }
    if (!currentTechId) {
      console.error('Cannot assign: Current user is not a tech');
      return;
    }

    setIsAssigning(true);
    try {
      const updatedTicket = await handleAssignTicket(ticket.id, currentTechId);
      setTicket(updatedTicket);
      await refreshActivities();
    } catch (err) {
      console.error('Failed to assign ticket:', err);
    } finally {
      setIsAssigning(false);
    }
  }, [ticket, currentTechId, handleAssignTicket, refreshActivities]);

  // Handle comment submission
  const onAddComment = useCallback(
    async (content: string) => {
      if (!ticket) return;
      await handleAddComment(ticket.id, content);
      await refreshActivities();
    },
    [ticket, handleAddComment, refreshActivities]
  );

  // Handle work note submission
  const onAddWorkNote = useCallback(
    async (content: string) => {
      if (!ticket) return;
      await handleAddWorkNote(ticket.id, content);
      await refreshActivities();
    },
    [ticket, handleAddWorkNote, refreshActivities]
  );

  // Handle asking a question to the user
  const onAskQuestion = useCallback(
    async (questionText: string) => {
      if (!ticket) return;
      await handleAskQuestion(ticket.id, questionText);
      // Refresh ticket (status may have changed to Waiting) and activities
      const updatedTicket = await ticketRepo.getById(ticket.id);
      if (updatedTicket) setTicket(updatedTicket);
      await refreshActivities();
    },
    [ticket, handleAskQuestion, ticketRepo, refreshActivities]
  );

  // Handle status change
  const onStatusChange = useCallback(
    async (
      newStatus: TicketStatus,
      options?: {
        waitingReason?: WaitingReason;
        waitingReasonNote?: string;
        resolutionNotes?: string;
      }
    ) => {
      if (!ticket) return;
      await handleStatusChange(ticket.id, newStatus, options);
      // Refresh ticket and activities
      const updatedTicket = await ticketRepo.getById(ticket.id);
      if (updatedTicket) setTicket(updatedTicket);
      await refreshActivities();
    },
    [ticket, handleStatusChange, ticketRepo, refreshActivities]
  );

  // Handle escalation
  const onEscalate = useCallback(
    async (newGroup: AssignmentGroup, reason: string) => {
      if (!ticket) return;
      await handleEscalateTicket(ticket.id, newGroup, reason);
      // Refresh ticket and activities
      const updatedTicket = await ticketRepo.getById(ticket.id);
      if (updatedTicket) setTicket(updatedTicket);
      await refreshActivities();
    },
    [ticket, handleEscalateTicket, ticketRepo, refreshActivities]
  );

  // Handle reassignment - triggers existing reassign modal flow
  const onReassign = useCallback(() => {
    if (!ticket) return;
    handleReassignTicket(ticket.id);
  }, [ticket, handleReassignTicket]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/ticketing/all-tickets');
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="it-ticket-view" data-theme={theme}>
        <div className="it-ticket-view__loading">
          <div className="it-ticket-view__spinner" />
          <span>Loading ticket details...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !ticket) {
    return (
      <div className="it-ticket-view" data-theme={theme}>
        <div className="it-ticket-view__error">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="24" cy="24" r="20" />
            <path d="M24 14v12M24 30v2" strokeLinecap="round" />
          </svg>
          <h2>{error ?? 'Ticket not found'}</h2>
          <p>We couldn't find the ticket you're looking for.</p>
          <button type="button" className="it-ticket-view__error-btn" onClick={handleBack}>
            Back to All Tickets
          </button>
        </div>
      </div>
    );
  }

  const displayNumber = formatTicketNumber(ticket.ticketNumber);

  return (
    <div className="it-ticket-view" data-theme={theme}>
      <div className="it-ticket-view__container">
        {/* Back navigation */}
        <button type="button" className="it-ticket-view__back" onClick={handleBack}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 15l-5-5 5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          All Tickets
        </button>

        {/* Ticket Banner */}
        <div className="it-ticket-view__banner">
          <div className="it-ticket-view__banner-left">
            <div className="it-ticket-view__ticket-badge">
              <svg className="it-ticket-view__ticket-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1.5a1.5 1.5 0 0 0 0 3V10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.5a1.5 1.5 0 0 0 0-3V4z" />
              </svg>
              <span className="it-ticket-view__ticket-label">Ticket</span>
            </div>
            <h1 className="it-ticket-view__ticket-id">{displayNumber}</h1>
          </div>
          <div className="it-ticket-view__banner-right">
            <span className={`it-ticket-view__status it-ticket-view__status--${ticket.status.toLowerCase().replace(' ', '-')}`}>
              {ticket.status}
            </span>
            {slaStatus && (
              <span className={`it-ticket-view__sla it-ticket-view__sla--${slaStatus}`}>
                {slaStatus === 'on_track' && 'On Track'}
                {slaStatus === 'at_risk' && 'At Risk'}
                {slaStatus === 'breached' && 'SLA Breached'}
                {slaStatus === 'met' && 'SLA Met'}
              </span>
            )}
            {/* Dynamic action button based on assignment state */}
            {isUnassigned && (
              <button
                type="button"
                className="it-ticket-view__action-btn it-ticket-view__action-btn--primary"
                onClick={onAssignToMe}
                disabled={isAssigning}
              >
                {isAssigning ? (
                  <>
                    <span className="it-ticket-view__action-spinner" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="8" cy="5" r="3" />
                      <path d="M3 14c0-3 2-5 5-5s5 2 5 5" strokeLinecap="round" />
                      <path d="M12 3v4M10 5h4" strokeLinecap="round" />
                    </svg>
                    Assign to me
                  </>
                )}
              </button>
            )}
            {isAssignedToOther && (
              <button
                type="button"
                className="it-ticket-view__action-btn"
                onClick={onReassign}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="6" cy="6" r="3" />
                  <path d="M2 14c0-2.5 1.5-4 4-4" strokeLinecap="round" />
                  <circle cx="11" cy="9" r="2.5" />
                  <path d="M8 14c0-2 1.2-3 3-3" strokeLinecap="round" />
                  <path d="M10 4l2 2-2 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Reassign
              </button>
            )}
            {isAssignedToMe && ticket.status !== TicketStatus.RESOLVED && ticket.status !== TicketStatus.CLOSED && (
              <button
                type="button"
                className="it-ticket-view__action-btn it-ticket-view__action-btn--success"
                onClick={() => onStatusChange(TicketStatus.RESOLVED)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 8l4 4 6-8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Resolve
              </button>
            )}
          </div>
        </div>

        {/* Two-column layout */}
        <div className="it-ticket-view__content">
          <div className="it-ticket-view__left">
            <TicketDetailsPanel
              ticket={ticket}
              assignedTech={assignedTech}
              onStatusChange={onStatusChange}
              onEscalate={onEscalate}
              onReassign={onReassign}
            />
          </div>
          <div className="it-ticket-view__right">
            <ActivityPanel
              ticket={ticket}
              activities={activities}
              currentUserId={currentUserId}
              currentUserName={currentUser?.name ?? 'IT Staff'}
              onAddComment={onAddComment}
              onAddWorkNote={onAddWorkNote}
              onAskQuestion={onAskQuestion}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Format ticket number for display by stripping common prefixes.
 * Handles formats like: TKT123456, INC123456, REQ123456
 */
function formatTicketNumber(ticketNumber: string): string {
  // Remove common ticket prefixes (TKT, INC, REQ, etc.)
  const prefixPattern = /^(TKT|INC|REQ|CHG|PRB|SR)/i;
  return ticketNumber.replace(prefixPattern, '');
}
